import React, { useCallback, useEffect, useState } from 'react';
import { Confirm, useLocaleState, useNotify, useTranslate } from 'react-admin';
import { Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { createDeviceShare, getDeviceShares, revokeDeviceShare } from '../../../rest/restClient';
import { getDateTimeToFullLocalString } from '../../../utils/date';


const DURATION_CHOICES = [
    { seconds: 3600, labelKey: 'resources.devices.shares.duration_1h' },
    { seconds: 28800, labelKey: 'resources.devices.shares.duration_8h' },
    { seconds: 86400, labelKey: 'resources.devices.shares.duration_24h' },
    { seconds: 604800, labelKey: 'resources.devices.shares.duration_7d' },
    { seconds: 2592000, labelKey: 'resources.devices.shares.duration_30d' },
];

const DEFAULT_DURATION_SECONDS = 86400;
const LABEL_MAX_LENGTH = 120;


const getDerivedStatus = (share) => {
    if (share.status && share.status.toUpperCase() === 'REVOKED') return 'revoked';
    if (share.expires_at && new Date(share.expires_at).getTime() < Date.now()) return 'expired';
    return 'active';
};


const getApiErrorMessage = (error, fallback) => {
    return error?.body?.description || error?.body?.message || fallback;
};


const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(textArea);
    }
};


const StatusChip = ({ share }) => {
    const translate = useTranslate();
    const derivedStatus = getDerivedStatus(share);
    const chipProps = {
        active: { color: 'success', variant: 'filled' },
        expired: { color: 'warning', variant: 'outlined' },
        revoked: { color: 'error', variant: 'outlined' },
    }[derivedStatus];
    return (
        <Chip
            size="small"
            label={translate(`resources.devices.shares.status_${derivedStatus}`)}
            {...chipProps}
        />
    );
};


const ShareCreateForm = ({ deviceId, onCreated }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [label, setLabel] = useState('');
    const [durationSeconds, setDurationSeconds] = useState(DEFAULT_DURATION_SECONDS);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = () => {
        if (label.length > LABEL_MAX_LENGTH) return;
        setSubmitting(true);
        createDeviceShare({ deviceId, label: label.trim() || undefined, durationSeconds })
            .then((response) => {
                setSubmitting(false);
                setLabel('');
                setDurationSeconds(DEFAULT_DURATION_SECONDS);
                onCreated(response.json);
            })
            .catch((error) => {
                setSubmitting(false);
                notify(getApiErrorMessage(error, translate('resources.devices.shares.create_error')), { type: 'error' });
            });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, margin: '0px 0px 10px 0px' }}>
                {translate('resources.devices.shares.create_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '10px', alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                <TextField
                    size="small"
                    fullWidth
                    label={translate('resources.devices.shares.label')}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    inputProps={{ maxLength: LABEL_MAX_LENGTH }}
                    helperText={`${label.length}/${LABEL_MAX_LENGTH}`}
                />
                <TextField
                    size="small"
                    select
                    sx={{ minWidth: '120px' }}
                    label={translate('resources.devices.shares.duration')}
                    value={durationSeconds}
                    onChange={(e) => setDurationSeconds(Number(e.target.value))}
                >
                    {DURATION_CHOICES.map((choice) => (
                        <MenuItem key={choice.seconds} value={choice.seconds}>
                            {translate(choice.labelKey)}
                        </MenuItem>
                    ))}
                </TextField>
                <Button
                    variant="contained"
                    disabled={submitting}
                    onClick={handleSubmit}
                    sx={{ whiteSpace: 'nowrap', minWidth: '110px' }}
                >
                    {translate('resources.devices.shares.create_button')}
                </Button>
            </Box>
        </Box>
    );
};


const ShareCreatedPanel = ({ share, onDismiss }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [locale] = useLocaleState();
    const [copied, setCopied] = useState(false);
    const [copiedFeedback, setCopiedFeedback] = useState(false);
    const [openCloseConfirm, setOpenCloseConfirm] = useState(false);

    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    const handleCopy = () => {
        copyToClipboard(share.url)
            .then(() => {
                setCopied(true);
                setCopiedFeedback(true);
                notify(translate('resources.devices.shares.link_copied'), { type: 'info' });
                setTimeout(() => setCopiedFeedback(false), 2000);
            })
            .catch(() => {
                notify(translate('resources.devices.shares.copy_error'), { type: 'warning' });
            });
    };

    const handleNativeShare = () => {
        navigator.share({
            text: translate('resources.devices.shares.share_message'),
            url: share.url,
        })
            .then(() => {
                setCopied(true);
            })
            .catch(() => {
                // share sheet dismissed or sharing failed; Copy stays available
            });
    };

    const handleDismiss = () => {
        if (!copied) {
            setOpenCloseConfirm(true);
            return;
        }
        onDismiss();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Alert severity="warning" sx={{ margin: '0px 0px 10px 0px' }}>
                {translate('resources.devices.shares.url_warning')}
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                <TextField
                    size="small"
                    fullWidth
                    value={share.url}
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '13px' } }}
                    onFocus={(e) => e.target.select()}
                />
                <Button
                    variant={canNativeShare ? 'outlined' : 'contained'}
                    color={copiedFeedback ? 'success' : 'primary'}
                    onClick={handleCopy}
                    sx={{ whiteSpace: 'nowrap', minWidth: '90px' }}
                >
                    {translate(copiedFeedback ? 'resources.devices.shares.copied' : 'resources.devices.shares.copy')}
                </Button>
            </Box>
            {canNativeShare && (
                <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ShareIcon />}
                    onClick={handleNativeShare}
                    sx={{ margin: '10px 0px 0px 0px' }}
                >
                    {translate('resources.devices.shares.share')}
                </Button>
            )}
            {share.expires_at && (
                <Typography sx={{ fontSize: '12px', margin: '10px 0px 0px 0px' }} color="text.secondary">
                    {`${translate('resources.devices.shares.expires_at')}: ${getDateTimeToFullLocalString({ timestamp: share.expires_at, locale })}`}
                </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0px 0px 0px' }}>
                <Button variant="text" onClick={handleDismiss}>
                    {translate('resources.devices.shares.done')}
                </Button>
            </Box>
            <Confirm
                isOpen={openCloseConfirm}
                title={translate('resources.devices.shares.close_without_copy_title')}
                content={translate('resources.devices.shares.close_without_copy')}
                onConfirm={() => {
                    setOpenCloseConfirm(false);
                    onDismiss();
                }}
                onClose={() => setOpenCloseConfirm(false)}
            />
        </Box>
    );
};


const ShareRow = ({ share, deviceId, onRevoked }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const theme = useTheme();
    const [locale] = useLocaleState();
    const [openRevokeConfirm, setOpenRevokeConfirm] = useState(false);
    const [revoking, setRevoking] = useState(false);

    const handleRevoke = () => {
        setRevoking(true);
        revokeDeviceShare({ deviceId, shareId: share.id })
            .then(() => {
                setRevoking(false);
                setOpenRevokeConfirm(false);
                notify(translate('resources.devices.shares.revoke_success'), { type: 'info' });
                onRevoked();
            })
            .catch((error) => {
                setRevoking(false);
                setOpenRevokeConfirm(false);
                notify(getApiErrorMessage(error, translate('resources.devices.shares.revoke_error')), { type: 'warning' });
            });
    };

    const detailLine = [
        share.created_at && `${translate('resources.devices.shares.created_at')}: ${getDateTimeToFullLocalString({ timestamp: share.created_at, locale })}`,
        share.expires_at && `${translate('resources.devices.shares.expires_at')}: ${getDateTimeToFullLocalString({ timestamp: share.expires_at, locale })}`,
    ].filter(Boolean);

    const usageLine = [
        `${translate('resources.devices.shares.view_count')}: ${share.view_count ?? 0}`,
        share.last_accessed_at && `${translate('resources.devices.shares.last_accessed_at')}: ${getDateTimeToFullLocalString({ timestamp: share.last_accessed_at, locale })}`,
    ].filter(Boolean);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', padding: '10px 0px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} color={share.label ? 'text.primary' : 'text.secondary'}>
                        {share.label || translate('resources.devices.shares.untitled')}
                    </Typography>
                    <StatusChip share={share} />
                </Box>
                {detailLine.length > 0 && (
                    <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                        {detailLine.join(' — ')}
                    </Typography>
                )}
                {usageLine.length > 0 && (
                    <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                        {usageLine.join(' — ')}
                    </Typography>
                )}
            </Box>
            {getDerivedStatus(share) === 'active' && (
                <Button
                    variant="text"
                    size="small"
                    disabled={revoking}
                    onClick={() => setOpenRevokeConfirm(true)}
                    sx={{ color: theme.palette.error.main, whiteSpace: 'nowrap' }}
                >
                    {translate('resources.devices.shares.revoke')}
                </Button>
            )}
            <Confirm
                isOpen={openRevokeConfirm}
                title={translate('resources.devices.shares.revoke_confirm_title')}
                content={translate('resources.devices.shares.revoke_confirm_content')}
                onConfirm={handleRevoke}
                onClose={() => setOpenRevokeConfirm(false)}
            />
        </Box>
    );
};


const ShareList = ({ shares, loading, loadError, deviceId, onRevoked }) => {
    const translate = useTranslate();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0px' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Alert severity="error" sx={{ margin: '10px 0px' }}>
                {translate('resources.devices.shares.load_error')}
            </Alert>
        );
    }

    if (!shares || shares.length === 0) {
        return (
            <Typography sx={{ fontSize: '14px', padding: '15px 0px', textAlign: 'center' }} color="text.secondary">
                {translate('resources.devices.shares.empty')}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {shares.map((share, index) => (
                <React.Fragment key={share.id}>
                    {index > 0 && <Divider />}
                    <ShareRow share={share} deviceId={deviceId} onRevoked={onRevoked} />
                </React.Fragment>
            ))}
        </Box>
    );
};


const DeviceShares = ({ deviceId, onClose }) => {
    const translate = useTranslate();
    const [shares, setShares] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [createdShare, setCreatedShare] = useState(null);

    const fetchShares = useCallback(() => {
        setLoadError(false);
        getDeviceShares({ deviceId })
            .then((response) => {
                setShares(response.json || []);
                setLoading(false);
            })
            .catch(() => {
                setLoadError(true);
                setLoading(false);
            });
    }, [deviceId]);

    useEffect(() => {
        setLoading(true);
        setShares(null);
        setCreatedShare(null);
        fetchShares();
    }, [fetchShares]);

    const handleCreated = (share) => {
        setCreatedShare(share);
        fetchShares();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {createdShare ? (
                <ShareCreatedPanel share={createdShare} onDismiss={() => setCreatedShare(null)} />
            ) : (
                <ShareCreateForm deviceId={deviceId} onCreated={handleCreated} />
            )}
            <Divider sx={{ margin: '15px 0px 5px 0px' }} />
            <ShareList
                shares={shares}
                loading={loading}
                loadError={loadError}
                deviceId={deviceId}
                onRevoked={fetchShares}
            />
            {onClose && !createdShare && (
                <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px 0px 0px 0px' }}>
                    <Button variant="text" onClick={onClose}>
                        {translate('ra.action.close')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};


export default DeviceShares;
